import { useMemo } from 'react';
import {
  inferEmotionalCategory,
  buildEmotionalSummary,
} from './emotionalContext';

interface Props {
  recipientName?: string;
  relationship?: string;
  senderLabel?: string;
  message: string;
  template?: string;
  animationTheme?: string;
  publishedDate?: string;
  photoUrls?: string[];
  /** Stable id used in JSON-LD @id when available */
  announcementId?: string | null;
}

/**
 * Invisible semantic + transcript layer for cinematic reveals.
 *
 * Renders:
 *   - A visually hidden but accessible <section> with full readable transcript,
 *     emotional summary, and semantic descriptors (for AI crawlers + screen readers).
 *   - Article + CreativeWork JSON-LD describing the emotional experience.
 *
 * NEVER paints pixels. The premium cinematic visual remains untouched.
 */
export function RevealSemanticLayer({
  recipientName,
  relationship,
  senderLabel,
  message,
  template,
  animationTheme,
  publishedDate,
  photoUrls,
  announcementId,
}: Props) {
  const ctx = useMemo(
    () =>
      inferEmotionalCategory({
        relationship,
        template,
        animationTheme,
        message,
      }),
    [relationship, template, animationTheme, message],
  );

  const summary = useMemo(
    () => buildEmotionalSummary({ ctx, recipientName, relationship, senderLabel }),
    [ctx, recipientName, relationship, senderLabel],
  );

  const wordCount = message?.trim().split(/\s+/).filter(Boolean).length ?? 0;

  const jsonLd = useMemo(() => {
    const id = announcementId
      ? `https://say-it-now.app/a/${announcementId}#emotional-experience`
      : undefined;
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          ...(id ? { '@id': id } : {}),
          headline: `${ctx.label}${recipientName ? ` for ${recipientName}` : ''}`,
          description: summary,
          articleBody: message,
          wordCount,
          inLanguage: 'en',
          datePublished: publishedDate ?? undefined,
          author: senderLabel
            ? { '@type': 'Person', name: senderLabel }
            : { '@type': 'Organization', name: 'Say It NOW' },
          publisher: {
            '@type': 'Organization',
            name: 'Say It NOW',
            url: 'https://say-it-now.app',
          },
          keywords: ctx.keywords.join(', '),
          about: [
            { '@type': 'Thing', name: ctx.label },
            { '@type': 'Thing', name: 'Living Announcement' },
            { '@type': 'Thing', name: 'Celebrate people while they are alive' },
          ],
          ...(photoUrls && photoUrls.length > 0 ? { image: photoUrls } : {}),
        },
        {
          '@type': 'CreativeWork',
          name: `${ctx.label}${recipientName ? ` for ${recipientName}` : ''}`,
          genre: ctx.category,
          text: message,
          abstract: summary,
          keywords: ctx.keywords.join(', '),
          inLanguage: 'en',
          isAccessibleForFree: true,
          accessibilityFeature: [
            'readingOrder',
            'structuralNavigation',
            'alternativeText',
            'transcript',
          ],
          accessibilityHazard: 'none',
        },
      ],
    };
  }, [ctx, summary, message, wordCount, recipientName, senderLabel, publishedDate, photoUrls, announcementId]);

  return (
    <>
      {/* JSON-LD: machine-readable emotional context for AI/search */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026") }}
      />

      {/* Visually hidden, but available to screen readers and crawlers */}
      <section
        className="sr-only"
        aria-label={`${ctx.label} transcript and emotional summary`}
        data-emotional-category={ctx.category}
        data-semantic-layer="cinematic-reveal"
      >
        <h2>{ctx.label}{recipientName ? ` for ${recipientName}` : ''}</h2>
        <p>
          <strong>Emotional summary:</strong> {summary}
        </p>
        {relationship && (
          <p>
            <strong>Relationship:</strong> {relationship}
          </p>
        )}
        {senderLabel && (
          <p>
            <strong>From:</strong> {senderLabel}
          </p>
        )}
        {publishedDate && (
          <p>
            <strong>Published:</strong>{' '}
            <time dateTime={publishedDate}>{publishedDate}</time>
          </p>
        )}
        <h3>Message transcript</h3>
        <blockquote>
          <p>{message}</p>
        </blockquote>
        <h3>Semantic descriptors</h3>
        <ul>
          {ctx.keywords.map((k) => (
            <li key={k}>{k}</li>
          ))}
        </ul>
        <p>
          This experience is a Living Announcement on Say It NOW — designed to
          help people celebrate the ones they love while they’re still here
          to read the words.
        </p>
      </section>
    </>
  );
}
